import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, gbp, fmtDate, downloadPdf, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, RefreshCw, BookOpen, FileSpreadsheet, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function SelfBillRegister() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showPaid, setShowPaid] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/self-bills-register").then((r) => setRows(r.data.rows)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updatePaidDate = async (r, date) => {
    setRows((curr) => curr.map((x) => x.id === r.id ? { ...x, paid_date: date, paid: !!date } : x));
    try {
      await api.patch(`/self-bills/${r.id}/paid`, { paid_date: date || null });
    } catch (e) {
      toast.error(formatApiError(e));
      setRows((curr) => curr.map((x) => x.id === r.id ? { ...x, paid_date: r.paid_date, paid: !!r.paid_date } : x));
    }
  };

  const filtered = rows.filter((r) => {
    const s = q.toLowerCase();
    if (!s) return true;
    return (
      r.name.toLowerCase().includes(s) ||
      r.driver_id.toLowerCase().includes(s) ||
      r.self_bill_number.toLowerCase().includes(s)
    );
  });

  const onExport = async () => {
    try {
      await downloadPdf("/self-bills-register/export.xlsx", "D4PS-Self-Bills-Register.xlsx");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sum = (list) => list.reduce((s, r) => s + (Number(r.total) || 0), 0);
  const unpaid = filtered.filter((r) => !r.paid_date);
  const paid = filtered.filter((r) => r.paid_date);
  const totalSum = sum(unpaid);
  const paidSum = sum(paid);

  const renderRow = (r) => (
    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`register-row-${r.id}`}>
      <td className="px-5 py-3 font-medium">{r.name || "—"}</td>
      <td className="px-5 py-3 text-slate-700">{r.driver_id || "—"}</td>
      <td className="px-5 py-3 font-medium text-slate-900">
        <Link to={`/self-bills/${r.id}`} className="hover:underline">{r.self_bill_number}</Link>
      </td>
      <td className="px-5 py-3 text-slate-700">{fmtDate(r.invoice_date)}</td>
      <td className="px-5 py-3 text-slate-700">
        {r.pay_period_from || r.pay_period_to ? `${fmtDate(r.pay_period_from)} – ${fmtDate(r.pay_period_to)}` : "—"}
      </td>
      <td className="px-5 py-3 text-right tabular-nums font-medium">{gbp(r.total)}</td>
      <td className="px-5 py-3">
        <Input data-testid={`register-paid-${r.id}`} type="date" value={r.paid_date || ""}
          onChange={(e) => updatePaidDate(r, e.target.value)}
          className={`h-8 rounded-sm border-slate-300 text-xs w-36 ${r.paid_date ? "bg-emerald-50 border-emerald-300 text-emerald-900" : ""}`} />
      </td>
    </tr>
  );

  return (
    <div data-testid="register-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Reporting</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-1 flex items-center gap-3">
            <BookOpen className="h-7 w-7 text-slate-700" />
            Self-Bills Register
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            Auto-updated record of every self-bill issued — replaces your Excel sheet.
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="register-refresh" variant="outline" className="rounded-sm border-slate-300" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button data-testid="register-export-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={onExport}>
            <FileSpreadsheet className="h-4 w-4 mr-2" /> Export Excel
          </Button>
        </div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="relative w-full max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="register-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, driver ID, or self-bill number…"
              className="pl-9 rounded-sm border-slate-300"
            />
          </div>
          <div className="ml-auto text-xs text-slate-500" data-testid="register-summary">
            {unpaid.length} unpaid · {gbp(totalSum)} outstanding · {paid.length} paid
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full po-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider">
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold">Driver ID</th>
                <th className="px-5 py-3 font-semibold">Self Bill #</th>
                <th className="px-5 py-3 font-semibold">Invoice Date</th>
                <th className="px-5 py-3 font-semibold">Pay Period</th>
                <th className="px-5 py-3 font-semibold text-right">Total</th>
                <th className="px-5 py-3 font-semibold">Date Paid</th>
              </tr>
            </thead>
            <tbody className="text-sm text-slate-900">
              {loading && (
                <tr><td colSpan="7" className="px-5 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-5 py-12 text-center">
                    <div className="text-slate-700 font-medium">No self-bills yet</div>
                    <div className="text-slate-500 text-sm mt-1">
                      Create a self-bill and it will appear here automatically.
                    </div>
                  </td>
                </tr>
              )}
              {!loading && filtered.length > 0 && unpaid.length === 0 && (
                <tr><td colSpan="7" className="px-5 py-6 text-center text-sm text-slate-500" data-testid="register-all-paid">All self-bills are paid.</td></tr>
              )}
              {!loading && unpaid.map(renderRow)}
              {!loading && paid.length > 0 && (
                <tr className="border-t border-slate-200 bg-slate-50 cursor-pointer select-none hover:bg-slate-100" onClick={() => setShowPaid((v) => !v)} data-testid="register-paid-toggle">
                  <td colSpan="5" className="px-5 py-3 text-xs uppercase tracking-wider font-semibold text-slate-600">
                    <span className="inline-flex items-center gap-2">
                      <ChevronRight className={`h-4 w-4 transition-transform ${showPaid ? "rotate-90" : ""}`} />
                      Paid self-bills ({paid.length})
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-sm font-medium text-emerald-700">{gbp(paidSum)}</td>
                  <td></td>
                </tr>
              )}
              {!loading && showPaid && paid.map(renderRow)}
            </tbody>
            {!loading && unpaid.length > 0 && (
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td colSpan="5" className="px-5 py-3 text-right text-xs uppercase tracking-wider font-semibold text-slate-600">
                    Unpaid total
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-display font-semibold text-slate-900">
                    {gbp(totalSum)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}
